import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuActivity,
  LuArrowLeft,
  LuArrowRight,
  LuCameraOff,
  LuCheck,
  LuCircleAlert,
  LuCircleCheck,
  LuClock,
  LuDownload,
  LuKeyboard,
  LuMic,
  LuPhoneOff,
  LuPlay,
  LuRefreshCw,
  LuSend,
  LuSparkles,
  LuTrendingUp,
  LuTriangleAlert,
  LuTrophy,
  LuVideo,
  LuVolume2,
  LuVolumeX,
  LuX,
} from "react-icons/lu";
import { Badge, Btn, Dialog, Note, Spinner } from "../components/Ui";
import "./InterviewVideo.css";

/* ============================================================
   Endpoints — PRESERVED 1:1 from the legacy video interview.
   - API_URL (v6): start / submit / end / Get_Feedback  (single tool endpoint)
   - FEEDBACK_API (v2): report data rendered on the results screen
     (matches legacy feedback.jsx which re-fetches from v2)
   - FEEDBACK_DOC_API (doc-v2): PDF report download
   ============================================================ */
const API_URL = "https://aspire-quest-interview-tool-v6-980069659423.us-east1.run.app";
const FEEDBACK_API = "https://aspire-quest-interview-tool-v2-980069659423.us-east1.run.app";
const FEEDBACK_DOC_API = "https://aspire-quest-interview-tool-doc-v2-980069659423.us-east1.run.app";
/* Server-side TTS for the interviewer voice. This is the primary voice path and
   works on every machine regardless of the browser's local speech voices; the
   browser SpeechSynthesis path is kept only as a fallback if this call fails. */
const TTS_API = "https://us-east1-foursssolutions.cloudfunctions.net/interview_tts_v2";
const TTS_VOICE = "onyx";

/* ---------- pure helpers (ported verbatim from legacy) ---------- */
const isDuplicate = (existingText, newText) => {
  if (!existingText || !newText) return false;
  const normalize = (text) => text.toLowerCase().trim().replace(/\s+/g, " ");
  return normalize(existingText).includes(normalize(newText));
};

const similarStrings = (str1, str2) => {
  if (!str1 || !str2) return false;
  const normalize = (text) => text.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
  const a = normalize(str1);
  const b = normalize(str2);
  return a.includes(b) || b.includes(a);
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const cameraMessage = (name) => {
  switch (name) {
    case "NotReadableError":
    case "TrackStartError":
      return "Your camera couldn't start. It may be open in another app or browser tab, or blocked by your OS camera privacy setting. Close other camera apps/tabs (or check Windows camera settings), then retry. You can also continue with audio only.";
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Camera access was blocked. Allow camera permission for this site in your browser, then retry.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No camera was found on this device. You can continue with audio only.";
    case "OverconstrainedError":
      return "Your camera couldn't meet the requested settings. Retry, or continue with audio only.";
    default:
      return "We couldn't start your camera. Check your camera and permissions, then retry, or continue with audio only.";
  }
};

const micMessage = (name) => {
  switch (name) {
    case "unsupported":
      return "Your browser doesn't support live voice capture. You can answer by typing instead.";
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Microphone access was blocked. Allow it and retry, or answer by typing.";
    case "NotReadableError":
    case "TrackStartError":
      return "Your microphone is being used by another app. Close it and retry, or answer by typing.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone was found on this device. You can answer by typing instead.";
    default:
      return "We couldn't access your microphone. Retry, or answer by typing.";
  }
};

const SPEECH_SUPPORTED = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

/* lightweight lifecycle logging so a stalled mic / recognizer is visible in
   devtools (search the console for "[IV]" to trace where the flow stops). */
const ivlog = (...args) => {
  // eslint-disable-next-line no-console
  console.log("[IV]", ...args);
};

/* ============================================================
   Avatar — a stylized CSS/SVG AI interviewer. Mint accent,
   theme-variable driven. Animates only while `speaking`.
   ============================================================ */
function Avatar({ speaking, thinking, muted, size = 116 }) {
  const cls = ["iv-avatar", speaking ? "is-speaking" : "", thinking ? "is-thinking" : ""].filter(Boolean).join(" ");
  return (
    <div className={cls} style={{ width: size, height: size }} aria-hidden="true">
      <span className="iv-avatar-ring" />
      <span className="iv-avatar-ring iv-avatar-ring-2" />
      <span className="iv-avatar-orb">
        <svg viewBox="0 0 120 120" width="100%" height="100%">
          <defs>
            <radialGradient id="ivFace" cx="42%" cy="34%" r="72%">
              <stop offset="0%" stopColor="var(--accent-strong)" />
              <stop offset="100%" stopColor="var(--accent)" />
            </radialGradient>
          </defs>
          <circle cx="60" cy="60" r="42" fill="url(#ivFace)" opacity="0.16" />
          <circle cx="60" cy="60" r="42" fill="none" stroke="var(--accent)" strokeWidth="2" opacity="0.55" />
          {/* eyes */}
          <circle className="iv-eye" cx="46" cy="52" r="4.4" fill="var(--accent-strong)" />
          <circle className="iv-eye" cx="74" cy="52" r="4.4" fill="var(--accent-strong)" />
          {/* equalizer mouth */}
          <g className="iv-mouth">
            <rect className="iv-bar iv-bar-1" x="45" y="68" width="4.5" height="10" rx="2.25" fill="var(--accent-strong)" />
            <rect className="iv-bar iv-bar-2" x="53" y="66" width="4.5" height="14" rx="2.25" fill="var(--accent-strong)" />
            <rect className="iv-bar iv-bar-3" x="61" y="66" width="4.5" height="14" rx="2.25" fill="var(--accent-strong)" />
            <rect className="iv-bar iv-bar-4" x="69" y="68" width="4.5" height="10" rx="2.25" fill="var(--accent-strong)" />
          </g>
        </svg>
      </span>
      {muted ? (
        <span className="iv-avatar-badge" title="Voice muted">
          <LuVolumeX size={13} />
        </span>
      ) : null}
    </div>
  );
}

/* ============================================================
   Report — post-interview feedback. Mirrors legacy feedback.jsx:
   re-fetches the report from the v2 endpoint and offers the
   doc-v2 PDF download. Rendered in the modern design.
   ============================================================ */
function Report({ docId, onRetake }) {
  const navigate = useNavigate();
  const [fb, setFb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const res = await fetch(FEEDBACK_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "Get_Feedback", doc_id: docId }),
        });
        if (res.ok) {
          const data = await res.json();
          if (alive) setFb(data);
        } else if (alive) {
          setError(true);
        }
      } catch (e) {
        if (alive) setError(true);
      } finally {
        if (alive) setTimeout(() => setLoading(false), 300);
      }
    })();
    return () => {
      alive = false;
    };
  }, [docId]);

  useEffect(() => load(), [load]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await fetch(FEEDBACK_DOC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "Get_Feedback_doc", doc_id: docId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.pdf_data) {
          const byteChars = atob(data.pdf_data);
          const byteNums = Array.from(byteChars).map((c) => c.charCodeAt(0));
          const blob = new Blob([new Uint8Array(byteNums)], { type: "application/pdf" });
          const now = new Date();
          const stamp = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `interview_report_${stamp}.pdf`;
          link.click();
          URL.revokeObjectURL(link.href);
        } else {
          toast.error("The report is not available yet. Please try again shortly.");
        }
      } else {
        toast.error("Could not download the analysis report.");
      }
    } catch (e) {
      toast.error("Could not download the analysis report.");
    } finally {
      setDownloading(false);
    }
  };

  const overall = Number((fb && fb.overall_score) || 85);
  const ratings = (fb && fb.ratings) || {};
  const strengths = (fb && fb.strengths) || [];
  const improvements = (fb && fb.areas_for_improvement) || [];
  const highlights = (fb && fb.analysis_highlights) || [];

  return (
    <div className="iv-report">
      <header className="iv-report-head">
        <span className="iv-report-check">
          <LuTrophy size={26} />
        </span>
        <h1 className="t-h1">Interview completed</h1>
        <p className="t-sm iv-report-sub">
          Thank you for completing your practice session. Below is an AI-generated analysis of your performance.
        </p>
      </header>

      {loading ? (
        <div className="iv-panel ld-glass iv-pad">
          <div className="stack-3">
            <div className="iv-skel" style={{ width: "45%", height: 20 }} />
            <div className="iv-skel" style={{ height: 12 }} />
            <div className="iv-skel" style={{ width: "80%", height: 12 }} />
            <div className="iv-skel" style={{ height: 90 }} />
          </div>
        </div>
      ) : error ? (
        <div className="iv-panel ld-glass iv-pad">
          <Note tone="danger">
            <span className="grow">We couldn't load your interview feedback.</span>
            <button type="button" className="iv-note-link" onClick={load}>
              <LuRefreshCw size={13} /> Retry
            </button>
          </Note>
        </div>
      ) : !fb || Object.keys(fb).length === 0 ? (
        <div className="iv-panel ld-glass iv-pad">
          <Note tone="info">No feedback is available for this interview yet.</Note>
        </div>
      ) : (
        <div className="iv-report-grid">
          <section className="iv-panel ld-glass iv-pad">
            <div className="iv-minihead">
              <LuActivity size={16} /> Interview analysis
            </div>
            <p className="t-xs mb-3">AI-powered assessment of your interview performance.</p>

            <div className="row-between mb-1">
              <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
                Overall performance
              </span>
              <span className="t-h3 t-accent">{overall}%</span>
            </div>
            <div className="bar mb-3">
              <span style={{ width: `${Math.max(0, Math.min(100, overall))}%` }} />
            </div>

            {strengths.length > 0 ? (
              <div className="mb-3">
                <div className="iv-subhead">Your strengths</div>
                <ul className="stack-2">
                  {strengths.map((item, i) => (
                    <li key={i} className="row t-sm" style={{ alignItems: "flex-start" }}>
                      <LuCircleCheck size={15} style={{ color: "var(--accent-strong)", marginTop: 3, flex: "0 0 auto" }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {improvements.length > 0 ? (
              <div className="mb-3">
                <div className="iv-subhead">Areas for improvement</div>
                <ul className="stack-2">
                  {improvements.map((item, i) => (
                    <li key={i} className="row t-sm" style={{ alignItems: "flex-start" }}>
                      <LuTrendingUp size={15} style={{ color: "var(--amber)", marginTop: 3, flex: "0 0 auto" }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {highlights.length > 0 ? (
              <div>
                <div className="iv-subhead">Key insights</div>
                <ol className="stack-2 iv-ol">
                  {highlights.map((item, i) => (
                    <li key={i} className="t-sm">
                      {item}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </section>

          <section className="stack-3">
            <div className="iv-panel ld-glass iv-pad">
              <div className="iv-minihead mb-3">
                <LuActivity size={16} /> Performance metrics
              </div>
              {Object.keys(ratings).length > 0 ? (
                <div className="stack-3">
                  {Object.entries(ratings).map(([key, value]) => (
                    <div key={key}>
                      <div className="row-between mb-1">
                        <span className="t-sm" style={{ textTransform: "capitalize" }}>
                          {key.replace(/_/g, " ")}
                        </span>
                        <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
                          {value}/10
                        </span>
                      </div>
                      <div className="bar">
                        <span style={{ width: `${(Number(value) / 10) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="t-sm">No metrics were recorded for this session.</p>
              )}
            </div>

            <div className="iv-panel ld-glass iv-pad">
              <div className="iv-minihead mb-2">
                <LuDownload size={16} /> Download report
              </div>
              <p className="t-xs mb-3">
                Download a detailed PDF of your interview analysis to review later or share with a mentor.
              </p>
              <Btn variant="ghost" className="btn-pill" onClick={downloadReport} loading={downloading}>
                <LuDownload size={16} /> Download analysis report
              </Btn>
            </div>
          </section>
        </div>
      )}

      <div className="iv-report-actions">
        <Btn variant="ghost" className="btn-pill" onClick={() => navigate("/aspire-quest")}>
          <LuArrowLeft size={15} /> Back to Aspire Quest
        </Btn>
        <Btn variant="mint" className="btn-pill" onClick={onRetake}>
          <LuPlay size={15} /> Start a new interview
        </Btn>
      </div>
    </div>
  );
}

/* ============================================================ */
export default function InterviewVideo() {
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.auth.auth.email);
  const { scenarioBased, interviewLevel, industryInterview } = useSelector((state) => state.profile);

  const configMissing = !interviewLevel;

  /* ---------- stage machine ---------- */
  const [stage, setStage] = useState("check"); // check | interview | processing | report
  const stageRef = useRef("check");
  const goStage = (s) => {
    stageRef.current = s;
    setStage(s);
  };

  /* ---------- start (first question) ---------- */
  const [preparing, setPreparing] = useState(false);
  const [startError, setStartError] = useState(false);
  const preparingRef = useRef(false);

  /* ---------- interview state ---------- */
  const [questions, setQuestions] = useState([]);
  const [docId, setDocId] = useState("");
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const questionsRef = useRef([]);
  const docIdRef = useRef("");
  const interviewCompleteRef = useRef(false);
  const elapsedTimeRef = useRef(0);
  const isSubmittingRef = useRef(false);
  const pendingAnswerRef = useRef("");

  /* ---------- recording / transcript ---------- */
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false); // candidate speaking
  const [transcript, setTranscript] = useState("");
  const [accumulatedTranscript, setAccumulatedTranscript] = useState("");
  const isListeningRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const transcriptRef = useRef("");
  const accumulatedTranscriptRef = useRef("");

  /* ---------- avatar / voice ---------- */
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);

  /* ---------- input mode / errors ---------- */
  const [micMode, setMicMode] = useState("voice"); // voice | text
  const [micError, setMicError] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState("");
  const micModeRef = useRef("voice");

  /* ---------- camera ---------- */
  const [cameraError, setCameraError] = useState(null);
  const [cameraKey, setCameraKey] = useState(0);

  /* ---------- system check ---------- */
  const [micLevel, setMicLevel] = useState(0);
  const [checkHeard, setCheckHeard] = useState(false);

  /* ---------- finalize / feedback ---------- */
  const [finalizeError, setFinalizeError] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);
  const [endReason, setEndReason] = useState(null); // short | insufficient
  const [count, setCount] = useState(null);
  const endReasonRef = useRef(null);

  /* ---------- dialogs ---------- */
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);

  /* ---------- refs: media / timers ---------- */
  const streamRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const cameraAutoRetryRef = useRef(0);
  const timerIntervalRef = useRef(null);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastTranscriptRef = useRef("");
  const transcriptScrollRef = useRef(null);
  const lastSpokenIndexRef = useRef(-1);
  const recognitionRestartAttempts = useRef(0);
  const recognitionManuallyStoppedRef = useRef(false);
  const keepAliveIntervalRef = useRef(null);
  const voiceActivityTimeoutRef = useRef(null);
  const lastSpeechTimeRef = useRef(Date.now());
  const currentUtteranceRef = useRef(null);
  /* server-side TTS playback (primary voice path) */
  const serverAudioRef = useRef(null);
  const serverAudioPlayingRef = useRef(false);
  const continuousSilenceRef = useRef(0);
  const previousTranscriptsRef = useRef([]);
  const answerStartTimeRef = useRef(0);
  const autoEndTimerRef = useRef(null);
  const speechActivityRef = useRef({ startTime: 0, totalDuration: 0, pauseStartTime: 0, isPaused: false });

  /* system-check mic meter refs */
  const checkStreamRef = useRef(null);
  const checkCtxRef = useRef(null);
  const checkAnalyserRef = useRef(null);
  const checkDataRef = useRef(null);
  const checkRafRef = useRef(null);
  const meterActiveRef = useRef(false); // guards StrictMode late-resolving getUserMedia

  /* recognition robustness timers */
  const listenWatchdogRef = useRef(null); // fires if recognition.onstart never happens
  const ttsFallbackRef = useRef(null); // fires if TTS onend never happens

  /* ---------- transcript helpers ---------- */
  const resetTranscript = () => {
    transcriptRef.current = "";
    accumulatedTranscriptRef.current = "";
    setTranscript("");
    setAccumulatedTranscript("");
    previousTranscriptsRef.current = [];
    answerStartTimeRef.current = 0;
  };

  const stopListeningFn = () => {
    ivlog("stopListeningFn");
    recognitionManuallyStoppedRef.current = true;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* noop */
      }
      recognitionRef.current = null;
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    if (voiceActivityTimeoutRef.current) {
      clearInterval(voiceActivityTimeoutRef.current);
      voiceActivityTimeoutRef.current = null;
    }
    if (listenWatchdogRef.current) {
      clearTimeout(listenWatchdogRef.current);
      listenWatchdogRef.current = null;
    }
    isListeningRef.current = false;
    setIsListening(false);
    setIsRecording(false);
    window.speechSynthesis.cancel();
    stopServerAudio();
  };

  /* ---------- submit an answer (PRESERVED: POST action:"submit") ---------- */
  const submitAnswers = async (answer) => {
    const acc = accumulatedTranscriptRef.current;
    const tr = transcriptRef.current;
    const completeAnswer = acc.trim()
      ? acc + (tr && !isDuplicate(acc, tr) ? " " + tr : "")
      : answer || tr;

    if (isSubmittingRef.current || !completeAnswer || interviewCompleteRef.current || preparingRef.current) {
      ivlog("submit skipped (guard)", {
        submitting: isSubmittingRef.current,
        hasAnswer: !!completeAnswer,
        complete: interviewCompleteRef.current,
        preparing: preparingRef.current,
      });
      return;
    }

    const qs = questionsRef.current;
    const currentQuestion = qs.length > 0 ? qs[qs.length - 1].question : "";
    if (similarStrings(currentQuestion, completeAnswer) || completeAnswer.trim().length <= 5) {
      ivlog("submit skipped (similar to question / too short)", completeAnswer);
      return;
    }

    pendingAnswerRef.current = completeAnswer;
    try {
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      setSubmitError(false);
      ivlog("submitting answer →", completeAnswer);
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submit",
          doc_id: docIdRef.current,
          answer: completeAnswer,
          elapsed_time: elapsedTimeRef.current,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setQuestions((prev) => {
          const updated = [...prev];
          if (updated.length > 0 && !similarStrings(updated[updated.length - 1].question, completeAnswer)) {
            updated[updated.length - 1].answer = completeAnswer;
          }
          if (data.next_question) {
            updated.push({ question: data.next_question, answer: "" });
          } else {
            interviewCompleteRef.current = true;
            setInterviewComplete(true);
            updated.push({ question: "Thank you for completing the interview.", answer: "" });
          }
          questionsRef.current = updated;
          return updated;
        });
        resetTranscript();
      } else {
        console.error("Error submitting answer", await response.text());
        setSubmitError(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setSubmitError(true);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const retrySubmit = () => {
    setSubmitError(false);
    submitAnswers(pendingAnswerRef.current);
  };

  /* ---------- speech recognition (PRESERVED transcript machine) ---------- */
  const startListeningFn = (isRestart = false) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      micModeRef.current = "text";
      setMicMode("text");
      setMicError("unsupported");
      return;
    }
    if (micModeRef.current === "text") return;
    ivlog("startListeningFn", { isRestart, stage: stageRef.current, micMode: micModeRef.current });

    if (!isRestart) {
      recognitionManuallyStoppedRef.current = false;
      answerStartTimeRef.current = 0;
      previousTranscriptsRef.current = [];
      accumulatedTranscriptRef.current = "";
      setAccumulatedTranscript("");
      speechActivityRef.current = { startTime: Date.now(), totalDuration: 0, pauseStartTime: 0, isPaused: false };
      continuousSilenceRef.current = 0;
    }

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      /* Prime/verify mic permission, but do NOT hold a second capture handle
         while the recognizer runs — release it immediately. Holding a parallel
         getUserMedia stream alongside webkitSpeechRecognition can starve the
         recognizer of audio on Chrome/Windows. */
      navigator.mediaDevices
        .getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000,
          },
        })
        .then((stream) => {
          ivlog("mic getUserMedia ok — releasing priming stream to the recognizer");
          stream.getTracks().forEach((t) => t.stop());
        })
        .catch((err) => {
          console.error("Error accessing microphone:", err);
          const name = (err && err.name) || "mic";
          ivlog("mic getUserMedia FAILED →", name);
          setMicError(name);
          micModeRef.current = "text";
          setMicMode("text");
        });

      recognition.onstart = () => {
        ivlog("recognition.onstart — listening is LIVE");
        if (listenWatchdogRef.current) {
          clearTimeout(listenWatchdogRef.current);
          listenWatchdogRef.current = null;
        }
        recognitionRestartAttempts.current = 0;
        isListeningRef.current = true;
        setIsListening(true);
        setIsRecording(true);
        lastSpeechTimeRef.current = Date.now();
        if (!isRestart) {
          isSpeakingRef.current = false;
          setIsSpeaking(false);
        }
      };

      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";
        let hasNewContent = false;

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
            hasNewContent = true;
          } else {
            interimTranscript += event.results[i][0].transcript;
            if (event.results[i][0].transcript.trim().length > 0) hasNewContent = true;
          }
        }

        const newTranscript = (finalTranscript + " " + interimTranscript).trim();
        transcriptRef.current = newTranscript;
        setTranscript(newTranscript);

        if (finalTranscript.trim().length > 0) {
          ivlog("onresult final →", finalTranscript.trim());
          setAccumulatedTranscript((prev) => {
            if (isDuplicate(prev, finalTranscript)) return prev;
            const separator = prev.length > 0 ? " " : "";
            const combined = prev + separator + finalTranscript.trim();
            previousTranscriptsRef.current.push(finalTranscript.trim());
            accumulatedTranscriptRef.current = combined;
            return combined;
          });
        }

        if (hasNewContent) {
          const now = Date.now();
          lastSpeechTimeRef.current = now;
          if (!isSpeakingRef.current && !answerStartTimeRef.current) answerStartTimeRef.current = now;
          if (speechActivityRef.current.isPaused) {
            continuousSilenceRef.current = 0;
            speechActivityRef.current.isPaused = false;
          }
          if (!isSpeakingRef.current) {
            isSpeakingRef.current = true;
            setIsSpeaking(true);
          }
        } else if (isSpeakingRef.current) {
          const now = Date.now();
          const timeSinceLastSpeech = now - lastSpeechTimeRef.current;
          if (timeSinceLastSpeech > 1500 && !speechActivityRef.current.isPaused) {
            speechActivityRef.current.isPaused = true;
            speechActivityRef.current.pauseStartTime = lastSpeechTimeRef.current;
          }
        }
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        ivlog("recognition.onerror →", event.error);
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          stopListeningFn();
          setMicError("NotAllowedError");
          micModeRef.current = "text";
          setMicMode("text");
          return;
        }
        if (event.error === "no-speech" || event.error === "audio-capture") {
          if (recognitionRestartAttempts.current < 5) {
            recognitionRestartAttempts.current += 1;
            setTimeout(() => {
              if (
                !recognitionManuallyStoppedRef.current &&
                !interviewCompleteRef.current &&
                stageRef.current === "interview"
              ) {
                startListeningFn(true);
              }
            }, 1000);
          } else {
            stopListeningFn();
            toast.error("We're having trouble with your microphone. You can answer by typing instead.");
            micModeRef.current = "text";
            setMicMode("text");
          }
        } else {
          stopListeningFn();
        }
      };

      recognition.onend = () => {
        ivlog("recognition.onend", {
          manuallyStopped: recognitionManuallyStoppedRef.current,
          complete: interviewCompleteRef.current,
          stage: stageRef.current,
        });
        isListeningRef.current = false;
        setIsListening(false);
        setIsRecording(false);
        if (!recognitionManuallyStoppedRef.current && !interviewCompleteRef.current && stageRef.current === "interview") {
          recognitionRestartAttempts.current += 1;
          if (recognitionRestartAttempts.current < 10) {
            setTimeout(() => startListeningFn(true), 500);
          } else {
            toast.error("Speech recognition hit too many errors. You can answer by typing instead.");
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            micModeRef.current = "text";
            setMicMode("text");
          }
        } else {
          recognitionRestartAttempts.current = 0;
          isSpeakingRef.current = false;
          setIsSpeaking(false);
        }
      };

      recognitionRef.current = recognition;
    }

    try {
      recognitionRef.current.start();
      ivlog("recognition.start() called — waiting for onstart");
      isListeningRef.current = true;
      setIsListening(true);

      /* watchdog: if onstart never fires the recognizer is inert (mic still
         held elsewhere, or a silent failure). Rebuild and retry a bounded
         number of times, then fall back to typed answers. */
      if (listenWatchdogRef.current) clearTimeout(listenWatchdogRef.current);
      listenWatchdogRef.current = setTimeout(() => {
        if (
          !isListeningRef.current &&
          !recognitionManuallyStoppedRef.current &&
          stageRef.current === "interview" &&
          micModeRef.current === "voice"
        ) {
          recognitionRestartAttempts.current += 1;
          if (recognitionRestartAttempts.current < 6) {
            ivlog("watchdog: onstart never fired — rebuilding recognizer", recognitionRestartAttempts.current);
            try {
              if (recognitionRef.current) recognitionRef.current.stop();
            } catch (e) {
              /* noop */
            }
            recognitionRef.current = null;
            startListeningFn(true);
          } else {
            ivlog("watchdog: giving up on voice → text mode");
            stopListeningFn();
            toast.error("We couldn't start voice capture. You can answer by typing instead.");
            micModeRef.current = "text";
            setMicMode("text");
          }
        }
      }, 2600);

      if (voiceActivityTimeoutRef.current) clearInterval(voiceActivityTimeoutRef.current);
      voiceActivityTimeoutRef.current = setInterval(() => {
        if (isListeningRef.current && !recognitionManuallyStoppedRef.current) {
          const now = Date.now();
          const timeSinceLastSpeech = now - lastSpeechTimeRef.current;
          if (isSpeakingRef.current && timeSinceLastSpeech > 1000) {
            continuousSilenceRef.current += 1;
            const acc = accumulatedTranscriptRef.current;
            const tr = transcriptRef.current;
            const hasContent = acc.trim().length > 10 || tr.trim().length > 10;
            if (continuousSilenceRef.current >= 4 && hasContent) {
              clearInterval(voiceActivityTimeoutRef.current);
              const finalAnswer = acc
                ? acc + (tr && !isDuplicate(acc, tr) ? " " + tr : "")
                : tr;
              ivlog("silence auto-submit →", finalAnswer.trim());
              submitAnswers(finalAnswer.trim());
              resetTranscript();
              stopListeningFn();
            }
          } else {
            continuousSilenceRef.current = 0;
          }
        }
      }, 1000);
    } catch (error) {
      /* "InvalidStateError: recognition has already started" means the
         recognizer IS live. Rebuilding here would orphan the running session
         and cause the start/abort/restart churn (VI-02). Keep the live one. */
      if (error && error.name === "InvalidStateError") {
        ivlog("recognition.start() — already started; keeping the live session");
        isListeningRef.current = true;
        setIsListening(true);
        return;
      }
      console.error("Failed to start recognition:", error);
      ivlog("recognition.start() threw — retrying", error && error.name);
      setTimeout(() => {
        recognitionRef.current = null;
        if (!recognitionManuallyStoppedRef.current && !interviewCompleteRef.current && stageRef.current === "interview") {
          startListeningFn(isRestart);
        }
      }, 1000);
    }
  };

  /* ---------- speak a question (SpeechSynthesis) ---------- */
  const applyPreferredVoice = (utterance) => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return; // no voices yet → keep the browser default
    const english = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
    const pool = english.length > 0 ? english : voices;
    /* Prefer ON-DEVICE (local) voices. Remote/network voices such as
       "Google US English" frequently fail silently when offline or rate-limited,
       which reads to the user as "the interviewer has no voice". Named local
       voices first (Windows ships these), then any local voice, then anything. */
    const preferredNames = [
      "Microsoft David",
      "Microsoft Mark",
      "Microsoft Guy",
      "Microsoft Aria",
      "Microsoft Zira",
      "Daniel",
      "Alex",
    ];
    const byName = preferredNames.map((n) => pool.find((v) => v.name && v.name.includes(n))).find(Boolean);
    const localVoice = pool.find((v) => v.localService);
    utterance.voice = byName || localVoice || pool[0];
  };

  const beginListeningAfterQuestion = () => {
    if (stageRef.current !== "interview" || micModeRef.current === "text") return;
    ivlog("beginListeningAfterQuestion scheduled (+800ms)");
    setTimeout(() => {
      if (stageRef.current !== "interview" || micModeRef.current === "text") return;
      if (isListeningRef.current) {
        ivlog("already listening — skip duplicate start");
        return;
      }
      resetTranscript();
      startListeningFn();
    }, 800);
  };

  const clearTtsFallback = () => {
    if (ttsFallbackRef.current) {
      clearTimeout(ttsFallbackRef.current);
      ttsFallbackRef.current = null;
    }
  };

  /* stop any in-flight server-audio playback and release the element.
     IMPORTANT: detach the media event handlers BEFORE clearing src. Setting
     src="" and calling load() makes the element fire an "error" event; if
     audio.onerror is still attached, that error handler runs even though the
     clip actually ENDED normally, and it triggers the browser-voice fallback —
     which re-reads the same question (the "double voice" bug, VI-01). Nulling
     the handlers here means a successful end can never fall through to a
     second voice. */
  const stopServerAudio = () => {
    if (serverAudioRef.current) {
      try {
        const a = serverAudioRef.current;
        a.onended = null;
        a.onerror = null;
        a.onplay = null;
        a.onloadedmetadata = null;
        a.pause();
        a.src = "";
        a.load();
      } catch (e) {
        /* noop */
      }
      serverAudioRef.current = null;
    }
    serverAudioPlayingRef.current = false;
  };

  /* ---------- FALLBACK voice: browser SpeechSynthesis ---------- */
  const speakWithBrowser = (text) => {
    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance(text);
      currentUtteranceRef.current = utterance;
      utterance.rate = 0.95;
      utterance.pitch = 0.98;
      utterance.volume = 1.0;
      applyPreferredVoice(utterance);

      utterance.onstart = () => {
        ivlog("TTS(browser) onstart");
        setAiSpeaking(true);
        isListeningRef.current = false;
        setIsListening(false);
        setIsRecording(false);
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {
            /* noop */
          }
        }
        keepAliveIntervalRef.current = setInterval(() => {
          const synth = window.speechSynthesis;
          if (synth.speaking) {
            synth.pause();
            synth.resume();
          } else {
            clearInterval(keepAliveIntervalRef.current);
          }
        }, 5000);
      };

      utterance.onend = () => {
        ivlog("TTS(browser) onend — opening mic");
        clearTtsFallback();
        setAiSpeaking(false);
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
        beginListeningAfterQuestion();
      };

      utterance.onerror = () => {
        ivlog("TTS(browser) onerror — opening mic");
        clearTtsFallback();
        setAiSpeaking(false);
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
        beginListeningAfterQuestion();
      };

      /* Speak IMMEDIATELY. Never withhold the utterance waiting for
         onvoiceschanged — on some machines that event never fires, which left
         the question silent. If voices aren't ready the browser uses its default
         voice. */
      if (window.speechSynthesis.getVoices().length === 0) {
        const upgrade = () => {
          window.speechSynthesis.removeEventListener("voiceschanged", upgrade);
        };
        window.speechSynthesis.addEventListener("voiceschanged", upgrade);
      }
      window.speechSynthesis.speak(utterance);
    }, 30);
  };

  /* ---------- PRIMARY voice: server-generated MP3 ---------- */
  /* Resolves true if the server audio took over the flow (its onended/onerror
     drive the mic handoff), false if the caller should fall back to browser. */
  const speakWithServer = async (text) => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      const res = await fetch(TTS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice: TTS_VOICE }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) return false;
      const data = await res.json();
      if (!data || data.status !== "ok" || !data.audio_base64) return false;

      /* muted or navigated away while the clip was generating: don't play, but
         treat as handled so we don't double up with the browser voice. */
      if (mutedRef.current || stageRef.current !== "interview") {
        setAiSpeaking(false);
        beginListeningAfterQuestion();
        return true;
      }

      const audio = new Audio(`data:${data.mime || "audio/mpeg"};base64,${data.audio_base64}`);
      serverAudioRef.current = audio;

      audio.onplay = () => {
        ivlog("TTS(server) playing");
        serverAudioPlayingRef.current = true;
        setAiSpeaking(true);
        /* interviewer is talking — make sure the recognizer isn't capturing */
        isListeningRef.current = false;
        setIsListening(false);
        setIsRecording(false);
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {
            /* noop */
          }
        }
      };
      audio.onloadedmetadata = () => {
        /* resize the safety net to the real clip length so a slow generate
           doesn't trip the length-estimate fallback mid-sentence. */
        clearTtsFallback();
        const durMs = (isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : 15000) + 3000;
        ttsFallbackRef.current = setTimeout(() => {
          ivlog("TTS(server) fallback — ended missed, opening mic");
          serverAudioPlayingRef.current = false;
          setAiSpeaking(false);
          stopServerAudio();
          beginListeningAfterQuestion();
        }, durMs);
      };
      audio.onended = () => {
        ivlog("TTS(server) ended — opening mic");
        /* the clip finished successfully — detach onerror so the teardown in
           stopServerAudio() (src="" + load()) can't fire a spurious "error"
           that would re-read the question via the browser voice (VI-01). */
        audio.onerror = null;
        clearTtsFallback();
        serverAudioPlayingRef.current = false;
        setAiSpeaking(false);
        stopServerAudio();
        beginListeningAfterQuestion();
      };
      audio.onerror = () => {
        ivlog("TTS(server) audio error — falling back to browser");
        serverAudioPlayingRef.current = false;
        stopServerAudio();
        speakWithBrowser(text);
      };

      await audio.play();
      return true;
    } catch (e) {
      ivlog("TTS(server) fetch failed — falling back to browser", e && e.name);
      return false;
    }
  };

  const speakQuestionFn = (text) => {
    window.speechSynthesis.cancel();
    stopServerAudio();
    clearTtsFallback();
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }

    /* muted: skip synthesis. In voice mode still open the mic so the answer
       is captured; in text mode this no-ops (candidate types). */
    if (mutedRef.current) {
      ivlog("speakQuestionFn: muted — skipping TTS, opening mic");
      setAiSpeaking(false);
      beginListeningAfterQuestion();
      return;
    }
    ivlog("speakQuestionFn: speaking question");
    setAiSpeaking(true); // avatar reacts while the clip is being generated

    /* safety net — if neither path ever opens the mic, do it anyway so the
       hands-free flow never dead-ends. Sized to the question length; the server
       path resizes this once it knows the real clip duration. */
    const estMs = Math.min(28000, Math.max(9000, text.length * 90));
    ttsFallbackRef.current = setTimeout(() => {
      ivlog("TTS fallback fired — opening mic anyway");
      setAiSpeaking(false);
      beginListeningAfterQuestion();
    }, estMs);

    /* server voice first (reliable everywhere); browser speech only if it fails */
    (async () => {
      const ok = await speakWithServer(text);
      if (!ok) speakWithBrowser(text);
    })();
  };

  /* ---------- timers ---------- */
  const startTimer = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    elapsedTimeRef.current = 0;
    setElapsedTime(0);
    timerIntervalRef.current = setInterval(() => {
      setElapsedTime((prev) => {
        const next = prev + 1;
        elapsedTimeRef.current = next;
        return next;
      });
    }, 1000);
  };

  /* ---------- full teardown (camera light must never stay on) ---------- */
  const cleanupResources = useCallback(() => {
    window.speechSynthesis.cancel();
    stopServerAudio();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* noop */
      }
      recognitionRef.current.onresult = null;
      recognitionRef.current.onend = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onstart = null;
      recognitionRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach((t) => t.stop());
      webcamStreamRef.current = null;
    }
    if (checkStreamRef.current) {
      checkStreamRef.current.getTracks().forEach((t) => t.stop());
      checkStreamRef.current = null;
    }
    if (checkCtxRef.current) {
      try {
        checkCtxRef.current.close();
      } catch (e) {
        /* noop */
      }
      checkCtxRef.current = null;
      checkAnalyserRef.current = null;
    }
    if (checkRafRef.current) {
      cancelAnimationFrame(checkRafRef.current);
      checkRafRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    if (voiceActivityTimeoutRef.current) {
      clearInterval(voiceActivityTimeoutRef.current);
      voiceActivityTimeoutRef.current = null;
    }
    if (autoEndTimerRef.current) {
      clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }
    if (listenWatchdogRef.current) {
      clearTimeout(listenWatchdogRef.current);
      listenWatchdogRef.current = null;
    }
    if (ttsFallbackRef.current) {
      clearTimeout(ttsFallbackRef.current);
      ttsFallbackRef.current = null;
    }
    recognitionRestartAttempts.current = 0;
    continuousSilenceRef.current = 0;
    isListeningRef.current = false;
  }, []);

  /* ---------- system-check mic meter ---------- */
  /* CRITICAL: the meter holds getUserMedia(audio) + an AudioContext. It must be
     torn down completely (tracks stopped AND context closed) before the
     interview stage starts webkitSpeechRecognition — an AudioContext lingering
     on the same mic is a common reason the recognizer captures nothing. Only
     ONE consumer may hold the mic at a time. */
  const stopMicMeter = useCallback(() => {
    meterActiveRef.current = false;
    if (checkRafRef.current) {
      cancelAnimationFrame(checkRafRef.current);
      checkRafRef.current = null;
    }
    if (checkStreamRef.current) {
      checkStreamRef.current.getTracks().forEach((t) => t.stop());
      checkStreamRef.current = null;
    }
    if (checkCtxRef.current) {
      try {
        checkCtxRef.current.close();
      } catch (e) {
        /* noop */
      }
      checkCtxRef.current = null;
      checkAnalyserRef.current = null;
    }
    setMicLevel(0);
    ivlog("mic meter stopped (mic released)");
  }, []);

  const startMicMeter = useCallback(async () => {
    if (!SPEECH_SUPPORTED) {
      ivlog("SpeechRecognition unsupported → text mode");
      micModeRef.current = "text";
      setMicMode("text");
      setMicError("unsupported");
      return;
    }
    meterActiveRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      /* if we were torn down while the permission prompt was open (StrictMode
         double-mount / fast Start click), stop the orphan stream so it can't
         keep the mic busy for the recognizer. */
      if (!meterActiveRef.current) {
        ivlog("meter getUserMedia resolved after teardown — stopping orphan stream");
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      checkStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      checkCtxRef.current = ctx;
      checkAnalyserRef.current = analyser;
      checkDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        if (!checkAnalyserRef.current || !checkDataRef.current) return;
        checkAnalyserRef.current.getByteFrequencyData(checkDataRef.current);
        const vol = checkDataRef.current.reduce((a, b) => a + b, 0) / checkDataRef.current.length;
        setMicLevel(vol);
        if (vol > 12) setCheckHeard(true);
        checkRafRef.current = requestAnimationFrame(loop);
      };
      checkRafRef.current = requestAnimationFrame(loop);
      setMicError(null);
      ivlog("mic meter started");
    } catch (error) {
      const name = (error && error.name) || "mic";
      ivlog("mic meter getUserMedia FAILED →", name);
      setMicError(name);
      micModeRef.current = "text";
      setMicMode("text");
    }
  }, []);

  /* run the mic meter only during the system-check stage */
  useEffect(() => {
    if (stage === "check") startMicMeter();
    return () => stopMicMeter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  /* Warm up TTS voices on mount. Chrome returns an empty getVoices() on the
     first call and populates them asynchronously; priming here means a preferred
     local voice is usually ready by the time the first question is spoken. */
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return undefined;
    synth.getVoices();
    const onVoices = () => synth.getVoices();
    synth.addEventListener?.("voiceschanged", onVoices);
    return () => synth.removeEventListener?.("voiceschanged", onVoices);
  }, []);

  /* ---------- START: fetch the first question (PRESERVED payload) ---------- */
  const startFn = useCallback(async () => {
    if (!userEmail || !interviewLevel) return;
    setStartError(false);
    preparingRef.current = true;
    setPreparing(true);
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "start",
          candidate_email: userEmail,
          candidate_name: "",
          com_ind_name: industryInterview,
          interview_level: interviewLevel,
          scenario_based: scenarioBased ? "Yes" : "No",
          job_desc: industryInterview,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const q = [{ question: data.first_question, answer: "" }];
        questionsRef.current = q;
        setQuestions(q);
        docIdRef.current = data.doc_id;
        setDocId(data.doc_id);
      } else {
        console.error("Error fetching interview", await response.text());
        setStartError(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setStartError(true);
    } finally {
      preparingRef.current = false;
      setPreparing(false);
    }
  }, [userEmail, interviewLevel, industryInterview, scenarioBased]);

  /* kick the start fetch on mount (legacy fired on [userEmail]) */
  useEffect(() => {
    if (userEmail && interviewLevel) startFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  /* ---------- enter the live interview ---------- */
  const startInterview = () => {
    ivlog("startInterview — releasing check-stage mic + camera before the interview mounts");
    /* release BOTH the mic meter and the check-stage webcam BEFORE the
       interview mounts, so the interview's recognizer/webcam acquire a free
       device (single-consumer handoff, avoids NotReadable / silent recognizer). */
    stopMicMeter();
    releaseWebcam();
    cameraAutoRetryRef.current = 0;
    goStage("interview");
    startTimer();
  };

  /* 31-minute auto-finish (PRESERVED) */
  useEffect(() => {
    if (stage !== "interview") return undefined;
    autoEndTimerRef.current = setTimeout(() => finalizeInterview(), 31 * 60 * 1000);
    return () => {
      if (autoEndTimerRef.current) {
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  /* speak the newest question when it arrives (PRESERVED) */
  useEffect(() => {
    if (stage !== "interview") return;
    if (questions.length > 0 && !interviewComplete) {
      const lastIndex = questions.length - 1;
      if (lastSpokenIndexRef.current === lastIndex) return;
      const lastQuestion = questions[lastIndex].question;
      if (
        lastQuestion === "Thank you for completing the interview." ||
        lastQuestion === "No more questions." ||
        !lastQuestion
      ) {
        interviewCompleteRef.current = true;
        setInterviewComplete(true);
        return;
      }
      // FIX: mark the question "spoken" only when we ACTUALLY speak it (inside
      // the timeout). Previously this ref was set here, synchronously, while the
      // real speak was deferred 1s. If the effect re-ran within that 1s (a
      // re-render / StrictMode double-invoke during the interview mount — which
      // is exactly what happens for the FIRST question), the pending speak was
      // cleared by the cleanup, but the question was already flagged as spoken,
      // so the re-run early-returned and the first question was permanently
      // silent. Setting the flag inside the timeout lets the re-run reschedule.
      const t = setTimeout(() => {
        lastSpokenIndexRef.current = lastIndex;
        speakQuestionFn(lastQuestion);
      }, 1000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, interviewComplete, stage]);

  /* avatar mouth animation follows real synthesis state */
  useEffect(() => {
    if (stage !== "interview" && stage !== "processing") return undefined;
    const id = setInterval(() => {
      const speaking =
        serverAudioPlayingRef.current || (window.speechSynthesis.speaking && !window.speechSynthesis.paused);
      setAiSpeaking((prev) => (prev !== speaking ? speaking : prev));
    }, 220);
    return () => clearInterval(id);
  }, [stage]);

  /* stable-transcript auto-submit (PRESERVED) */
  useEffect(() => {
    if (!isListening || transcript.trim().length === 0) return undefined;
    lastTranscriptRef.current = transcript;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (transcript.trim().length > 10) {
      timeoutRef.current = setTimeout(() => {
        if (lastTranscriptRef.current === transcript) {
          const qs = questionsRef.current;
          const currentQuestion = qs.length > 0 ? qs[qs.length - 1].question : "";
          if (!similarStrings(currentQuestion, transcript)) {
            const acc = accumulatedTranscriptRef.current;
            const completeAnswer = acc
              ? acc + (transcript && !isDuplicate(acc, transcript) ? " " + transcript : "")
              : transcript;
            setQuestions((prev) => {
              const updated = [...prev];
              if (updated.length > 0) updated[updated.length - 1].answer = completeAnswer;
              questionsRef.current = updated;
              return updated;
            });
            submitAnswers(completeAnswer.trim());
            resetTranscript();
            stopListeningFn();
          }
        }
      }, 5000);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript, isListening, questions, accumulatedTranscript]);

  /* natural completion (API returned no next_question) → feedback */
  useEffect(() => {
    if (interviewComplete && stageRef.current === "interview") {
      window.speechSynthesis.cancel();
      stopServerAudio();
      stopListeningFn();
      goStage("processing");
      const t = setTimeout(() => getFeedback(), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewComplete]);

  /* keep the transcript scrolled to the newest content */
  useEffect(() => {
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight;
    }
  }, [questions, transcript, accumulatedTranscript]);

  /* ---------- countdown redirect (PRESERVED timings) ---------- */
  useEffect(() => {
    if (count === 0) {
      navigate("/aspire-quest");
      return undefined;
    }
    if (count !== null) {
      const timer = setTimeout(() => setCount((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [count, navigate]);

  const startCountdown = () => {
    setTimeout(() => setCount(5), 500);
  };

  /* ---------- GET FEEDBACK (PRESERVED: v6 Get_Feedback + status gate) ---------- */
  const getFeedback = async () => {
    if (endReasonRef.current) return; // short-interview branch already redirecting
    setFeedbackError(false);
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "Get_Feedback", doc_id: docIdRef.current }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === "error") {
          endReasonRef.current = "insufficient";
          setEndReason("insufficient");
          startCountdown();
          return;
        }
        goStage("report");
      } else {
        console.error("Error fetching feedback", await response.text());
        setFeedbackError(true);
      }
    } catch (error) {
      console.error("Network error:", error);
      setFeedbackError(true);
    }
  };

  /* ---------- FINALIZE (PRESERVED: POST action:"end" + <6min gate) ---------- */
  const finalizeInterview = () => {
    setFinishConfirmOpen(false);
    setFinalizeError(false);
    goStage("processing");
    window.speechSynthesis.cancel();
    stopServerAudio();
    stopListeningFn();

    (async () => {
      if (isSubmittingRef.current || interviewCompleteRef.current) return;
      try {
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidate_email: userEmail,
            action: "end",
            doc_id: docIdRef.current,
            base64_video: "",
          }),
        });
        if (response.ok) {
          await response.json();
          interviewCompleteRef.current = true;
          setInterviewComplete(true);
          setQuestions((prev) => {
            const updated = [...prev, { question: "Thank you for completing the interview.", answer: "" }];
            questionsRef.current = updated;
            return updated;
          });
          if (elapsedTimeRef.current < 360) {
            endReasonRef.current = "short";
            setEndReason("short");
            startCountdown();
            return;
          }
          setTimeout(() => getFeedback(), 2000);
        } else {
          console.error("Error ending interview", await response.text());
          setFinalizeError(true);
        }
      } catch (error) {
        console.error("Error:", error);
        setFinalizeError(true);
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    })();
  };

  /* ---------- manual answer submit ---------- */
  const submitCurrentAnswer = () => {
    const acc = accumulatedTranscriptRef.current;
    const tr = transcriptRef.current;
    const completeAnswer = (acc ? acc + (tr && !isDuplicate(acc, tr) ? " " + tr : "") : tr).trim();
    if (completeAnswer.length <= 5) {
      toast.info("I didn't catch a full answer yet — keep going, then submit.");
      return;
    }
    setQuestions((prev) => {
      const updated = [...prev];
      if (updated.length > 0) updated[updated.length - 1].answer = completeAnswer;
      questionsRef.current = updated;
      return updated;
    });
    submitAnswers(completeAnswer);
    resetTranscript();
    stopListeningFn();
  };

  const submitTypedAnswer = () => {
    const ans = typedAnswer.trim();
    if (ans.length <= 5) {
      toast.info("Please type a longer answer before submitting.");
      return;
    }
    setQuestions((prev) => {
      const updated = [...prev];
      if (updated.length > 0) updated[updated.length - 1].answer = ans;
      questionsRef.current = updated;
      return updated;
    });
    submitAnswers(ans);
    setTypedAnswer("");
  };

  /* ---------- voice mute toggle ---------- */
  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      mutedRef.current = next;
      if (next) {
        window.speechSynthesis.cancel();
        stopServerAudio();
        setAiSpeaking(false);
      }
      return next;
    });
  };

  /* ---------- cancel / abandon ---------- */
  const confirmEndAndLeave = () => {
    setEndConfirmOpen(false);
    endReasonRef.current = "left";
    cleanupResources();
    navigate("/aspire-quest");
  };

  /* ---------- retake ---------- */
  const resetForRetake = () => {
    cleanupResources();
    interviewCompleteRef.current = false;
    setInterviewComplete(false);
    questionsRef.current = [];
    setQuestions([]);
    docIdRef.current = "";
    setDocId("");
    elapsedTimeRef.current = 0;
    setElapsedTime(0);
    endReasonRef.current = null;
    setEndReason(null);
    setCount(null);
    setFinalizeError(false);
    setFeedbackError(false);
    setSubmitError(false);
    lastSpokenIndexRef.current = -1;
    resetTranscript();
    setCheckHeard(false);
    goStage("check");
    startFn();
  };

  /* ---------- camera handlers ---------- */
  const releaseWebcam = () => {
    try {
      if (webcamStreamRef.current) {
        webcamStreamRef.current.getTracks().forEach((t) => t.stop());
        webcamStreamRef.current = null;
      }
    } catch (e) {
      /* noop */
    }
  };
  const onWebcamStream = (stream) => {
    webcamStreamRef.current = stream;
    cameraAutoRetryRef.current = 0; // camera came up cleanly
    setCameraError(null);
  };
  const onWebcamError = (err) => {
    const name = (err && (err.name || err.message)) || "camera";
    console.error("Webcam error:", err);
    /* NotReadableError/TrackStartError is usually a transient device lock —
       most often React StrictMode's dev double-mount racing the previous
       webcam instance's not-yet-released stream. Silently auto-retry a couple
       of times (releasing our own stream first) before surfacing the error to
       the user, so a genuine "device busy" still shows but the dev race heals. */
    const transient = name === "NotReadableError" || name === "TrackStartError" || name === "AbortError";
    if (transient && cameraAutoRetryRef.current < 3) {
      cameraAutoRetryRef.current += 1;
      releaseWebcam();
      setTimeout(() => setCameraKey((k) => k + 1), 500);
      return;
    }
    setCameraError(name);
  };
  const retryCamera = () => {
    /* release any track we may still be holding before remounting the webcam,
       otherwise our own stale stream can keep the camera "in use" and the
       retry fails with the same NotReadableError */
    releaseWebcam();
    cameraAutoRetryRef.current = 0;
    setCameraError(null);
    setTimeout(() => setCameraKey((k) => k + 1), 150);
  };

  /* ---------- global cleanup / tab-close ---------- */
  useEffect(() => {
    window.addEventListener("beforeunload", cleanupResources);
    return () => {
      cleanupResources();
      window.removeEventListener("beforeunload", cleanupResources);
    };
  }, [cleanupResources]);

  /* ============================================================
     RENDER
     ============================================================ */
  const questionCount = questions.length;
  const currentQuestion = questionCount > 0 ? questions[questionCount - 1].question : "";
  const liveText = `${accumulatedTranscript ? accumulatedTranscript + " " : ""}${
    transcript && !isDuplicate(accumulatedTranscript, transcript) ? transcript : ""
  }`.trim();

  const EndButton = (
    <Btn variant="danger-soft" className="btn-pill" size="sm" onClick={() => setEndConfirmOpen(true)}>
      <LuPhoneOff size={14} /> End interview
    </Btn>
  );

  /* ---------- config missing (deep-linked without a session) ---------- */
  if (configMissing) {
    return (
      <div className="csx">
        <div className="csx-page iv-center">
          <div className="iv-panel ld-glass iv-pad iv-config">
            <span className="ld-stat-icon">
              <LuVideo size={20} />
            </span>
            <h1 className="t-h2 mt-3">No interview configured</h1>
            <p className="t-sm mt-1">
              Choose a track and configure a session in Aspire Quest, then start your video interview from there.
            </p>
            <div className="mt-3">
              <Btn to="/aspire-quest" variant="mint" className="btn-pill">
                <LuArrowRight size={15} /> Go to Aspire Quest
              </Btn>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="csx">
      <div className="csx-page iv-page">
        {/* ---------- top bar ---------- */}
        <header className="iv-topbar">
          <div className="row" style={{ gap: 10 }}>
            <span className="ld-stat-icon iv-topicon">
              <LuVideo size={17} />
            </span>
            <div>
              <div className="t-h3">Video interview</div>
              <div className="t-xs" style={{ textTransform: "capitalize" }}>
                {interviewLevel} level{scenarioBased ? " · scenario-based" : ""}
              </div>
            </div>
          </div>
          <div className="row" style={{ gap: 10 }}>
            {stage === "interview" ? (
              <>
                <span className="iv-timer" title="Elapsed time">
                  <LuClock size={14} /> {formatTime(elapsedTime)}
                </span>
                <Badge tone="plain">
                  Q{Math.max(1, questionCount)}
                </Badge>
              </>
            ) : null}
            {stage !== "report" ? EndButton : null}
          </div>
        </header>

        <main className="iv-main">
          {/* ================= SYSTEM CHECK ================= */}
          {stage === "check" ? (
            <div className="iv-check-grid">
              <section className="iv-panel ld-glass iv-pad">
                <span className="ld-eyebrow">Before you start</span>
                <h2 className="t-h2 mt-1">System check</h2>
                <p className="t-sm mt-1 mb-3">
                  We'll make sure your camera and microphone are ready. You can end the interview at any time.
                </p>

                <div className="iv-cam-wrap">
                  {cameraError ? (
                    <div className="iv-camerr">
                      <span className="iv-camerr-icon">
                        <LuCameraOff size={22} />
                      </span>
                      <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                        Camera unavailable
                      </div>
                      <p className="t-xs iv-camerr-msg">{cameraMessage(cameraError)}</p>
                      <div className="row mt-2" style={{ gap: 8 }}>
                        <Btn variant="ghost" className="btn-pill" size="sm" onClick={retryCamera}>
                          <LuRefreshCw size={13} /> Retry camera
                        </Btn>
                      </div>
                    </div>
                  ) : (
                    <Webcam
                      key={cameraKey}
                      className="iv-cam"
                      audio={false}
                      mirrored
                      onUserMedia={onWebcamStream}
                      onUserMediaError={onWebcamError}
                    />
                  )}
                </div>
              </section>

              <section className="iv-panel ld-glass iv-pad">
                <div className="iv-check-item">
                  <span className="row t-sm t-ink" style={{ fontWeight: 600 }}>
                    <LuVideo size={16} style={{ color: "var(--accent)" }} /> Camera
                  </span>
                  {cameraError ? (
                    <Badge tone="amber">
                      <LuTriangleAlert size={12} /> Unavailable
                    </Badge>
                  ) : (
                    <Badge tone="accent">
                      <LuCheck size={12} /> Ready
                    </Badge>
                  )}
                </div>

                <div className="iv-check-item">
                  <span className="row t-sm t-ink" style={{ fontWeight: 600 }}>
                    <LuMic size={16} style={{ color: "var(--accent)" }} /> Microphone
                  </span>
                  {micError ? (
                    <Badge tone="amber">
                      <LuTriangleAlert size={12} /> Unavailable
                    </Badge>
                  ) : checkHeard ? (
                    <Badge tone="accent">
                      <LuCheck size={12} /> We can hear you
                    </Badge>
                  ) : (
                    <Badge tone="plain">Listening…</Badge>
                  )}
                </div>

                {!micError ? (
                  <div className="iv-meter" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, micLevel * 1.6)}%` }} />
                  </div>
                ) : null}
                {!micError ? <p className="t-xs mt-1">Say a few words to test your microphone.</p> : null}

                {micError ? (
                  <div className="mt-2">
                    <Note tone="amber">
                      <LuTriangleAlert size={15} />
                      <span className="grow">{micMessage(micError)}</span>
                    </Note>
                    <p className="t-xs mt-1">You'll answer by typing during the interview.</p>
                  </div>
                ) : null}

                <div className="iv-check-voice mt-3">
                  <span className="t-sm">Interviewer voice</span>
                  <Btn variant="ghost" className="btn-pill" size="sm" onClick={toggleMute}>
                    {muted ? <LuVolumeX size={14} /> : <LuVolume2 size={14} />}
                    {muted ? "Muted" : "On"}
                  </Btn>
                </div>

                {startError ? (
                  <div className="mt-3">
                    <Note tone="danger">
                      <span className="grow">We couldn't prepare your interview questions.</span>
                      <button type="button" className="iv-note-link" onClick={startFn}>
                        <LuRefreshCw size={13} /> Retry
                      </button>
                    </Note>
                  </div>
                ) : preparing ? (
                  <div className="mt-3">
                    <Note tone="info">
                      <Spinner />
                      <span className="grow">Preparing your interview questions…</span>
                    </Note>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Note tone="accent">
                      <LuCircleCheck size={15} />
                      <span className="grow">Your interview is ready when you are.</span>
                    </Note>
                  </div>
                )}

                <div className="iv-check-actions mt-3">
                  <Btn variant="ghost" className="btn-pill" onClick={() => setEndConfirmOpen(true)}>
                    Cancel
                  </Btn>
                  <Btn variant="mint" className="btn-pill" size="lg" onClick={startInterview} disabled={startError}>
                    <LuPlay size={16} /> Start interview
                  </Btn>
                </div>
              </section>
            </div>
          ) : null}

          {/* ================= LIVE INTERVIEW ================= */}
          {stage === "interview" ? (
            <div className="iv-stage-grid">
              {/* interviewer */}
              <section className="iv-panel ld-glass iv-pad iv-interviewer">
                <div className="row-between">
                  <span className="ld-kicker">
                    <LuSparkles size={13} /> AI interviewer
                  </span>
                  <button
                    type="button"
                    className="iv-voice-toggle"
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute interviewer voice" : "Mute interviewer voice"}
                    title={muted ? "Unmute voice" : "Mute voice"}
                  >
                    {muted ? <LuVolumeX size={16} /> : <LuVolume2 size={16} />}
                  </button>
                </div>

                <div className="iv-avatar-stage">
                  <Avatar speaking={aiSpeaking} thinking={isSubmitting || preparing} muted={muted} />
                  <div className={`iv-speak-pill${aiSpeaking ? " on" : ""}`}>
                    {aiSpeaking ? "Speaking…" : muted ? "Voice muted" : "Ready"}
                  </div>
                </div>

                <div className="iv-question">
                  <div className="iv-question-label">
                    Question {Math.max(1, questionCount)}
                  </div>
                  {preparing && questionCount === 0 ? (
                    <p className="iv-question-text iv-muted">Preparing your first question…</p>
                  ) : (
                    <p className="iv-question-text">{currentQuestion}</p>
                  )}
                </div>
              </section>

              {/* candidate */}
              <section className="iv-panel ld-glass iv-pad iv-candidate">
                <div className="iv-cam-wrap iv-cam-live">
                  {cameraError ? (
                    <div className="iv-camerr">
                      <span className="iv-camerr-icon">
                        <LuCameraOff size={22} />
                      </span>
                      <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                        Camera unavailable
                      </div>
                      <p className="t-xs iv-camerr-msg">{cameraMessage(cameraError)}</p>
                      <div className="row mt-2" style={{ gap: 8 }}>
                        <Btn variant="ghost" className="btn-pill" size="sm" onClick={retryCamera}>
                          <LuRefreshCw size={13} /> Retry camera
                        </Btn>
                      </div>
                      <p className="t-xs mt-1">Your interview continues with audio only.</p>
                    </div>
                  ) : (
                    <Webcam
                      key={`live-${cameraKey}`}
                      className="iv-cam"
                      audio={false}
                      mirrored
                      onUserMedia={onWebcamStream}
                      onUserMediaError={onWebcamError}
                    />
                  )}
                  {!cameraError && micMode === "voice" ? (
                    <span className={`iv-rec${isRecording ? " on" : ""}`}>
                      <span className="iv-rec-dot" /> {isRecording ? "Recording" : "Paused"}
                    </span>
                  ) : null}
                </div>

                {/* answer area */}
                {micMode === "text" ? (
                  <div className="iv-answer">
                    <div className="iv-answer-head">
                      <span className="row t-sm t-ink" style={{ fontWeight: 600, gap: 6 }}>
                        <LuKeyboard size={15} /> Type your answer
                      </span>
                    </div>
                    <textarea
                      className="input iv-textarea"
                      placeholder="Type your answer here, then submit to get the next question…"
                      value={typedAnswer}
                      onChange={(e) => setTypedAnswer(e.target.value)}
                      disabled={isSubmitting || interviewComplete}
                    />
                    <div className="row mt-2" style={{ justifyContent: "flex-end" }}>
                      <Btn
                        variant="mint"
                        className="btn-pill"
                        onClick={submitTypedAnswer}
                        loading={isSubmitting}
                        disabled={interviewComplete}
                      >
                        <LuSend size={15} /> Submit answer
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <div className="iv-answer">
                    <div className="iv-answer-head">
                      <span className="row t-sm t-ink" style={{ fontWeight: 600, gap: 6 }}>
                        <LuMic size={15} style={{ color: isRecording ? "var(--accent)" : "var(--ink-3)" }} />
                        {isSpeaking ? "Listening — keep going" : isRecording ? "Listening…" : "Your answer"}
                      </span>
                    </div>
                    <div className="iv-transcript" ref={transcriptScrollRef}>
                      {liveText ? (
                        <p className="iv-transcript-text">{liveText}</p>
                      ) : (
                        <p className="iv-transcript-text iv-muted">
                          {aiSpeaking
                            ? "Listen to the question…"
                            : "Your live transcript will appear here as you speak."}
                        </p>
                      )}
                    </div>
                    <div className="row mt-2" style={{ justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                      <span className="t-xs iv-hint">
                        Your answer sends automatically after a short pause, or submit it now.
                      </span>
                      <Btn
                        variant="mint"
                        className="btn-pill"
                        size="sm"
                        onClick={submitCurrentAnswer}
                        loading={isSubmitting}
                        disabled={interviewComplete}
                      >
                        <LuSend size={14} /> Submit answer
                      </Btn>
                    </div>
                  </div>
                )}

                {submitError ? (
                  <div className="mt-2">
                    <Note tone="danger">
                      <span className="grow">We couldn't send your answer.</span>
                      <button type="button" className="iv-note-link" onClick={retrySubmit}>
                        <LuRefreshCw size={13} /> Retry
                      </button>
                    </Note>
                  </div>
                ) : null}

                <div className="iv-live-actions mt-3">
                  <Btn variant="danger-soft" className="btn-pill" onClick={() => setEndConfirmOpen(true)}>
                    <LuPhoneOff size={15} /> End interview
                  </Btn>
                  <Btn variant="mint" className="btn-pill" onClick={() => setFinishConfirmOpen(true)} disabled={isSubmitting}>
                    <LuTrophy size={15} /> Finish &amp; get feedback
                  </Btn>
                </div>
              </section>
            </div>
          ) : null}

          {/* ================= PROCESSING / TERMINAL ================= */}
          {stage === "processing" ? (
            <div className="iv-center-stage">
              <div className="iv-panel ld-glass iv-pad iv-process">
                {endReason === "short" || endReason === "insufficient" ? (
                  <>
                    <span className="iv-process-clock">
                      <LuClock size={26} />
                    </span>
                    <h2 className="t-h2 mt-2">Interview duration notice</h2>
                    {endReason === "short" ? (
                      <p className="t-sm mt-2 iv-process-copy">
                        Please take the interview for at least 6 minutes to receive proper feedback. Currently, we don't
                        have enough data to generate feedback for you for this interview.
                      </p>
                    ) : (
                      <p className="t-sm mt-2 iv-process-copy">
                        Not enough conversation data to generate meaningful feedback. The interview should have multiple
                        question-answer exchanges.
                      </p>
                    )}
                    <p className="t-xs mt-3">Redirecting you to Aspire Quest in {count}s</p>
                    <div className="mt-3">
                      <Btn variant="mint" className="btn-pill" onClick={() => setCount(0)}>
                        Go to Aspire Quest now <LuArrowRight size={15} />
                      </Btn>
                    </div>
                  </>
                ) : finalizeError ? (
                  <>
                    <span className="iv-process-clock iv-err">
                      <LuCircleAlert size={26} />
                    </span>
                    <h2 className="t-h2 mt-2">We couldn't end the interview</h2>
                    <p className="t-sm mt-2 iv-process-copy">
                      There was a network problem while wrapping up. You can retry, or head back to Aspire Quest.
                    </p>
                    <div className="row mt-3" style={{ gap: 10, justifyContent: "center" }}>
                      <Btn variant="ghost" className="btn-pill" onClick={() => navigate("/aspire-quest")}>
                        <LuArrowLeft size={15} /> Aspire Quest
                      </Btn>
                      <Btn variant="mint" className="btn-pill" onClick={finalizeInterview}>
                        <LuRefreshCw size={15} /> Retry
                      </Btn>
                    </div>
                  </>
                ) : feedbackError ? (
                  <>
                    <span className="iv-process-clock iv-err">
                      <LuCircleAlert size={26} />
                    </span>
                    <h2 className="t-h2 mt-2">We couldn't load your feedback</h2>
                    <p className="t-sm mt-2 iv-process-copy">
                      Your interview was submitted, but we hit a problem generating the report. Try again in a moment.
                    </p>
                    <div className="row mt-3" style={{ gap: 10, justifyContent: "center" }}>
                      <Btn variant="ghost" className="btn-pill" onClick={() => navigate("/aspire-quest")}>
                        <LuArrowLeft size={15} /> Aspire Quest
                      </Btn>
                      <Btn variant="mint" className="btn-pill" onClick={getFeedback}>
                        <LuRefreshCw size={15} /> Retry
                      </Btn>
                    </div>
                  </>
                ) : (
                  <>
                    <Avatar speaking={aiSpeaking} thinking muted={muted} size={104} />
                    <h2 className="t-h2 mt-3">Analyzing your interview</h2>
                    <p className="t-sm mt-2 iv-process-copy">
                      We're reviewing your answers and preparing your personalized feedback. This only takes a moment.
                    </p>
                    <div className="row mt-3" style={{ gap: 8, justifyContent: "center" }}>
                      <Spinner /> <span className="t-sm">Generating feedback…</span>
                    </div>
                    <div className="mt-3">
                      <Btn variant="ghost" className="btn-pill" size="sm" onClick={() => navigate("/aspire-quest")}>
                        Back to Aspire Quest
                      </Btn>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {/* ================= REPORT ================= */}
          {stage === "report" ? <Report docId={docId} onRetake={resetForRetake} /> : null}
        </main>

        {/* ---------- confirm: end & leave ---------- */}
        <Dialog open={endConfirmOpen} onClose={() => setEndConfirmOpen(false)}>
          <div className="row-between mb-2">
            <div className="t-h3">End the interview?</div>
            <button type="button" onClick={() => setEndConfirmOpen(false)} aria-label="Close" style={{ color: "var(--ink-3)" }}>
              <LuX size={18} />
            </button>
          </div>
          <p className="t-sm">
            Leaving now stops your camera and microphone and returns you to Aspire Quest. Any progress in this session may
            be lost and no feedback will be generated.
          </p>
          <div className="row mt-3" style={{ gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" className="btn-pill" onClick={() => setEndConfirmOpen(false)}>
              Keep going
            </Btn>
            <Btn variant="danger" className="btn-pill" onClick={confirmEndAndLeave}>
              <LuPhoneOff size={15} /> End interview
            </Btn>
          </div>
        </Dialog>

        {/* ---------- confirm: finish & get feedback ---------- */}
        <Dialog open={finishConfirmOpen} onClose={() => setFinishConfirmOpen(false)}>
          <div className="row-between mb-2">
            <div className="t-h3">Finish and get feedback?</div>
            <button type="button" onClick={() => setFinishConfirmOpen(false)} aria-label="Close" style={{ color: "var(--ink-3)" }}>
              <LuX size={18} />
            </button>
          </div>
          <p className="t-sm">
            We'll wrap up your interview and generate your feedback report now. For the most useful analysis, an interview
            should run at least 6 minutes with a few question-and-answer exchanges.
          </p>
          <div className="row mt-3" style={{ gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" className="btn-pill" onClick={() => setFinishConfirmOpen(false)}>
              Not yet
            </Btn>
            <Btn variant="mint" className="btn-pill" onClick={finalizeInterview}>
              <LuTrophy size={15} /> Finish &amp; get feedback
            </Btn>
          </div>
        </Dialog>
      </div>
    </div>
  );
}
